import { Grid, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import {
  formatCurrency,
  getCancellationPolicyDistribution,
  getHeadingStyle,
  getSupplyByChannel,
  getSupplyByPropertyType,
} from 'src/utils/functions';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_400,
  COLOR_PRIMARY_1_500,
  COLOR_PRIMARY_1_600,
  COLOR_PRIMARY_1_700,
  COLOR_PRIMARY_1_800,
  COLOR_TERTIARY_1_100,
  COLOR_TERTIARY_1_200,
  COLOR_TERTIARY_1_300,
  COLOR_TERTIARY_1_400,
  COLOR_TERTIARY_1_500,
  COLOR_TEXT_2,
  GRAPH_BLUE,
} from 'src/colors';
import { DynamicPieChart } from 'src/App/Components/Charts';
import PieDataCard from '../../../HOC/Cards/PieDataCard';
import { VisibleListingsProp, VisibleOTAListingsProp } from 'src/interfaces/iproptypes';
import { PieChartData } from 'src/interfaces/idashboard';
import { LoadingCard } from 'src/App/HOC';
import NoDataCard from 'src/App/HOC/Cards/NoDataCard';

const PropertyTypeChannelPieChart = ({
  listings,
  otaListings,
  cachedData,
  viewMode,
  isDynamicLoading,
  multipleMarkersCallLoading,
  noDataInMarket,
  totalListingsCount,
}: {
  listings: VisibleListingsProp;
  otaListings: VisibleOTAListingsProp;
  cachedData: {
    property_type?: { [key: string]: number } | null;
    cancellation_policy?: { [key: string]: number } | null;
    channel_type?: { [key: string]: number } | null;
  } | null;
  viewMode: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  isDynamicLoading: boolean;
  multipleMarkersCallLoading: Boolean;
  noDataInMarket: boolean;
  totalListingsCount: number;
}) => {
  const isDataStaticPropertyType =
    viewMode === 'dashboard' && cachedData && cachedData.property_type;
  const isDataLoadingPropertyType =
    multipleMarkersCallLoading && !listings.data?.length && !isDataStaticPropertyType;

  const supplyByPropertyType = useMemo(() => {
    if (isDataLoadingPropertyType) return [];
    return getSupplyByPropertyType(
      listings.data,
      isDataStaticPropertyType && cachedData ? cachedData.property_type : null
    );
  }, [listings.data, viewMode, cachedData]);

  const isDataStaticChannelType = viewMode === 'dashboard' && cachedData;

  // const supplyByChannel = useMemo(() => {
  //   return getSupplyByChannel(
  //     listings.data,
  //     otaListings.data,
  //     isDataStaticChannelType && cachedData ? cachedData.channel_type : null
  //   );
  // }, [listings, otaListings, cachedData, viewMode]);

  const isDataStaticCancellationPolicy =
    viewMode === 'dashboard' && cachedData && cachedData.cancellation_policy;
  const isDataLoadingCancellationPolicy = !listings.data?.length && !isDataStaticCancellationPolicy;

  const cancellationPolicyDistribution = useMemo(() => {
    if (isDataLoadingCancellationPolicy) {
      return [];
    }
    return getCancellationPolicyDistribution(
      listings.data,
      isDataStaticCancellationPolicy ? cachedData.cancellation_policy : null
    );
  }, [listings.data, viewMode, cachedData]);

  const getCancellationPolicyTooltipText = (value: number) => {
    const numberOfListings = formatCurrency(String(Math.round((value / 100) * totalListingsCount)));
    return `${numberOfListings} listings ( ${value}% )`;
  };

  const pieChartRadius: number = 60;
  return (
    <>
      <Grid item container className="subSection--spacing">
        {noDataInMarket ? (
          <NoDataCard height={150} borderRadius={8} />
        ) : isDataLoadingPropertyType || isDynamicLoading ? (
          <LoadingCard height={150} type="bars" />
        ) : (
          <Grid
            container
            direction="column"
            justifyContent="center"
            item
            xs={12}
            spacing={1}
            sx={{
              padding: '5px',
              boxShadow: '0 4px 10px #0003',
              borderRadius: 1,
            }}
          >
            <Grid item container sx={{ width: '100%' }}>
              <Grid item xs={12}>
                <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                  By Property Type
                </Typography>

                <DynamicPieChart
                  data={supplyByPropertyType}
                  unit={'listings'}
                  pieColors={[
                    COLOR_PRIMARY_1_800,
                    COLOR_PRIMARY_1_700,
                    COLOR_PRIMARY_1_600,
                    COLOR_PRIMARY_1_500,
                    COLOR_PRIMARY_1_400,
                  ]}
                  pieAccentColor={COLOR_PRIMARY_1}
                  label={'Property Type'}
                  radius={pieChartRadius}
                  parent="listing_by_property_type"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      {/* <Grid item container className="subSection--spacing">
        {noDataInMarket ? (
          <NoDataCard height={150} borderRadius={8} />
        ) : (supplyByChannel.length == 0 && multipleMarkersCallLoading) || isDynamicLoading ? (
          <LoadingCard height={150} type="bars" />
        ) : (
          <Grid
            container
            direction="column"
            justifyContent="center"
            item
            xs={12}
            spacing={1}
            sx={{
              marginTop: '5px',
              padding: '5px',
              boxShadow: '0 4px 10px #0003',
              borderRadius: 1,
            }}
          >
            <Grid item container sx={{ width: '100%' }}>
              <Grid item xs={12}>
                <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                  By Channel Type
                </Typography>

                <DynamicPieChart
                  data={supplyByChannel}
                  unit={'listings'}
                  pieColors={[
                    COLOR_TERTIARY_1_400,
                    COLOR_TERTIARY_1_300,
                    COLOR_TERTIARY_1_200,
                    COLOR_TERTIARY_1_100,
                  ]}
                  pieAccentColor={COLOR_TERTIARY_1_500}
                  label={'Channel'}
                  radius={pieChartRadius}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid> */}

      <Grid item container className="subSection--spacing">
        {noDataInMarket ? (
          <NoDataCard height={150} borderRadius={8} />
        ) : (isDataLoadingCancellationPolicy && multipleMarkersCallLoading) || isDynamicLoading ? (
          <LoadingCard height={150} type="bars" />
        ) : (
          <Grid
            container
            direction="column"
            justifyContent="center"
            item
            xs={12}
            spacing={1}
            sx={{
              marginTop: '5px',
              padding: '5px',
              boxShadow: '0 4px 10px #0003',
              borderRadius: 1,
            }}
          >
            <Grid item container sx={{ width: '100%' }}>
              <Grid item xs={12}>
                <Typography variant="body1" sx={getHeadingStyle(COLOR_TEXT_2)}>
                  By Cancellation Policy Type
                </Typography>

                <PieDataCard
                  tooltipContent="Market cancellation policies"
                  data={cancellationPolicyDistribution}
                  unit="%"
                  pieColors={[GRAPH_BLUE, GRAPH_BLUE, GRAPH_BLUE]}
                  pieAccentColor={GRAPH_BLUE + 'CC'}
                  label={'Cancellation Policy'}
                  radius={pieChartRadius}
                  getTooltipText={getCancellationPolicyTooltipText}
                  parent="listing_by_cancellation_policy"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default React.memo(PropertyTypeChannelPieChart);
